import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { refreshGiftsEnabled } from "@/hooks/useGiftsEnabled";

const when = (s) => (s ? new Date(s).toLocaleString("en-GB") : "—");

function Rows({ items, testid, empty }) {
  if (!items.length) return <p className="text-[#8C84A0]" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left text-[#C9C2D8]" data-testid={testid}>
        <thead className="text-[#B57BFF]"><tr><th className="py-2 pr-4">Type</th><th className="pr-4">To</th><th className="pr-4">Attempts</th><th className="pr-4">Last error</th><th>Created</th></tr></thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} className="border-t border-[#2E2140]" data-testid={`email-row-${e.id}`}>
              <td className="py-2 pr-4">{e.kind}</td><td className="pr-4">{e.to}</td><td className="pr-4">{e.attempts}</td>
              <td className="pr-4">{e.last_error || "—"}</td><td>{when(e.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GiftRows() {
  const [gifts, setGifts] = useState(null);
  const [sw, setSw] = useState({ enabled: false, can: false, busy: false });
  useEffect(() => { api.get("/admin/gifts").then((r) => { setGifts(r.data.gifts); setSw({ enabled: r.data.enabled, can: r.data.can_switch, busy: false }); }).catch(() => setGifts(false)); }, []);
  const toggle = async () => {
    const next = !sw.enabled;
    if (!window.confirm(next ? "Switch gift vouchers ON? Customers will be able to buy and redeem gifts straight away." : "Switch gift vouchers OFF? The gift pages and links will be hidden. Existing gifts are kept.")) return;
    setSw((v) => ({ ...v, busy: true }));
    try { const r = await api.post("/admin/gifts/enabled", { enabled: next }); setSw((v) => ({ ...v, enabled: r.data.enabled, busy: false })); refreshGiftsEnabled(); }
    catch (e) { window.alert(apiError(e)); setSw((v) => ({ ...v, busy: false })); }
  };
  if (!gifts) return null;
  return (
    <section id="gifts" className="glass-card p-6 scroll-mt-24" data-testid="admin-gifts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-lg text-[#EDE6F7]">Gift vouchers ({gifts.length})</h2>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${sw.enabled ? "text-[#C9B6FF]" : "text-[#8C84A0]"}`} data-testid="admin-gifts-status">{sw.enabled ? "ON: customers can buy and redeem" : "OFF: hidden from customers"}</span>
          {sw.can && <button type="button" onClick={toggle} disabled={sw.busy} data-testid="admin-gifts-toggle"
            className="rounded-full border border-[#8B2CFF] px-4 py-1.5 text-sm text-[#EDE6F7] hover:bg-[#241634] disabled:opacity-60">{sw.enabled ? "Switch off" : "Switch on"}</button>}
        </div>
      </div>
      {!gifts.length ? <p className="text-[#8C84A0]" data-testid="admin-gifts-empty">No gift vouchers yet.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm text-left text-[#C9C2D8]">
          <thead className="text-[#B57BFF]"><tr><th className="py-2 pr-4">Code</th><th className="pr-4">Status</th><th className="pr-4">From</th><th className="pr-4">For</th><th className="pr-4">Email to recipient</th><th className="pr-4">Paid</th><th className="pr-4">Expires</th><th>Redeemed</th></tr></thead>
          <tbody>{gifts.map((g) => (
            <tr key={g.id} className="border-t border-[#2E2140]" data-testid={`admin-gift-row-${g.code}`}>
              <td className="py-2 pr-4 font-semibold whitespace-nowrap">{g.code}</td><td className="pr-4">{g.state}</td><td className="pr-4">{g.buyer_name} ({g.buyer_email})</td>
              <td className="pr-4">{g.recipient_name}<span className="block text-xs text-[#8C84A0]">{(g.items || ["base"]).join(" + ")}</span></td><td className="pr-4">{g.recipient_email ? `${g.recipient_email_status} · ${g.send_on}` : "—"}</td>
              <td className="pr-4">{when(g.paid_at)}</td><td className="pr-4">{when(g.expires_at)}</td><td>{g.redeemed_at ? `${when(g.redeemed_at)} · ${g.redeemed_by_email || ""}` : "—"}</td>
            </tr>))}</tbody>
        </table></div>)}
    </section>
  );
}

export default function AdminEmails() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/admin/email-log").then((r) => setData(r.data)).catch((e) => setError(apiError(e))); }, []);
  useEffect(() => { if (data && window.location.hash === "#gifts") setTimeout(() => document.getElementById("gifts")?.scrollIntoView({ behavior: "smooth" }), 300); }, [data]);
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="admin-emails">
        <div>
          <Link to="/admin" className="text-[#B57BFF] underline underline-offset-4" data-testid="admin-back-link">← Admin dashboard</Link>
          <p className="eyebrow mb-2 mt-4">Administrator</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#EDE6F7]">Email delivery</h1>
          <p className="text-[#B9B3C8] mt-2">“Accepted” means the email provider took the message. It doesn't confirm it reached the inbox.</p>
        </div>
        {error && <p className="text-[#E6CCF2]" data-testid="admin-emails-error">{error}</p>}
        {data && (
          <>
            <p className="text-[#C9C2D8]" data-testid="admin-emails-accepted">Accepted by the provider: {data.accepted_count}</p>
            <section className="glass-card p-6"><h2 className="text-lg text-[#EDE6F7] mb-3">Failed after all retries</h2>
              <Rows items={data.failed} testid="admin-emails-failed" empty="No failed emails." /></section>
            <section className="glass-card p-6"><h2 className="text-lg text-[#EDE6F7] mb-3">Waiting to retry</h2>
              <Rows items={data.pending} testid="admin-emails-pending" empty="Nothing waiting." /></section>
          </>
        )}
        <GiftRows />
      </main>
    </div>
  );
}
